import type { ApiError } from '@/lib/problem-details'

import type { CustomerFieldPath } from './wizard-steps'

/**
 * Turning a rejected submission into something a data-entry screen can act on.
 *
 * The server answers with a stable dotted code and, for model-level failures, a field-keyed
 * validation problem. Both are translated here into the form's own field paths so a refusal lands
 * on the control that caused it rather than in a banner above six steps of captured data.
 *
 * The wording is short on purpose: it sits under a field, next to a customer who is waiting.
 */

interface CodeMapping {
  path?: CustomerFieldPath
  message: string
}

const CODE_MAPPINGS: Record<string, CodeMapping> = {
  'customers.ghana_card_required': {
    path: 'ghanaCardNumber',
    message: 'A Ghana Card is required for a Ghanaian resident.',
  },
  'customers.ghana_card_invalid': {
    path: 'ghanaCardNumber',
    message: 'That number fails the Ghana Card format or its check character. Re-read it from the card.',
  },
  'customers.ghana_card_duplicate': {
    path: 'ghanaCardNumber',
    message: 'Already held by another customer. The same person cannot be onboarded twice.',
  },
  'customers.phone_duplicate': {
    path: 'phoneNumber',
    message: 'Already held by another customer, at this branch or another one.',
  },
  'customers.phone_invalid': {
    path: 'phoneNumber',
    message: 'Not a recognisable Ghanaian mobile number.',
  },
  'customers.underage': {
    path: 'dateOfBirth',
    message: 'Below the minimum age for an account in a personal name.',
  },
  'customers.residence_address_required': {
    path: 'residence.streetOrArea',
    message: 'A residence address is required before this record can be held.',
  },
  'customers.consent_required': {
    path: 'consentAccountOperation',
    message: 'Account-operation consent was not captured.',
  },
  'customers.blacklisted': {
    message: 'This person is blacklisted across the institution, and the operation was refused.',
  },
  'customers.not_found': {
    message: 'That record no longer exists, or it belongs to a branch you cannot see.',
  },
}

/**
 * Server-side member names, lower-cased, against the form path they belong to. ASP.NET Core's
 * validation problem keys are PascalCase and sometimes carry a container prefix, so only the last
 * segment is matched.
 */
const MEMBER_PATHS: Record<string, CustomerFieldPath> = {
  branchid: 'branchId',
  firstname: 'firstName',
  middlename: 'middleName',
  lastname: 'lastName',
  gender: 'gender',
  dateofbirth: 'dateOfBirth',
  placeofbirth: 'placeOfBirth',
  nationality: 'nationality',
  maritalstatus: 'maritalStatus',
  phonenumber: 'phoneNumber',
  altphonenumber: 'altPhoneNumber',
  email: 'email',
  ghanacardnumber: 'ghanaCardNumber',
  occupation: 'occupation',
  employerorbusinessname: 'employerOrBusinessName',
  monthlyincomeestimateminor: 'monthlyIncomeEstimateMinor',
}

export interface FieldMessage {
  path: CustomerFieldPath
  message: string
}

export interface MappedProblem {
  /** Messages to attach to controls. */
  fields: FieldMessage[]
  /** What could not be attached to a control, for the banner above the form. */
  unattached: string[]
}

const lastSegment = (key: string) => {
  const withoutIndexes = key.replace(/\[\d+\]/g, '')
  const segments = withoutIndexes.split('.')
  return (segments[segments.length - 1] ?? '').toLowerCase()
}

export function mapCustomerProblem(error: ApiError): MappedProblem {
  const fields: FieldMessage[] = []
  const unattached: string[] = []

  const mapping = CODE_MAPPINGS[error.code]
  if (mapping) {
    if (mapping.path) {
      fields.push({ path: mapping.path, message: mapping.message })
    } else {
      unattached.push(mapping.message)
    }
  }

  for (const [key, messages] of Object.entries(error.fieldErrors ?? {})) {
    const path = MEMBER_PATHS[lastSegment(key)]

    for (const message of messages) {
      if (path) {
        fields.push({ path, message })
      } else {
        // Keeping the server's own key is deliberate: an unrecognised member name means the two
        // sides have drifted, and hiding it would make that drift look like a user mistake.
        unattached.push(key && key !== '$' ? `${key}: ${message}` : message)
      }
    }
  }

  return { fields, unattached }
}
